<template>
  <div class="chart-container-root">
    <!-- ================= FILTER HEADER ================= -->
    <div class="filter-header">
      <div class="filter-controls-left">
        <div class="segmented-control">
          <button
            v-for="opt in ['DAY', 'WEEK', 'MONTH']"
            :key="opt"
            :class="{ active: date === opt }"
            @click="date = opt"
          >
            {{ opt.charAt(0) + opt.slice(1).toLowerCase() }}
          </button>
        </div>

        <div class="preset-dropdown">
          <select v-model="selectedPreset" @change="applyPreset" class="custom-select">
            <option value="">Custom Range</option>
            <option value="TODAY">Today</option>
            <option value="YESTERDAY">Yesterday</option>
            <option value="7_DAYS">Last 7 Days</option>
            <option value="15_DAYS">Last 15 Days</option>
            <option value="30_DAYS">Last 30 Days</option>
            <option value="90_DAYS">Last 90 Days</option>
            <option value="THIS_WEEK">This Week</option>
            <option value="LAST_WEEK">Last Week</option>
            <option value="THIS_MONTH">This Month</option>
            <option value="LAST_MONTH">Last Month</option>
            <option value="3_MONTHS">Last 3 Months</option>
            <option value="6_MONTHS">Last 6 Months</option>
            <option value="THIS_YEAR">This Year ({{ new Date().getFullYear() }})</option>
            <option value="LAST_YEAR">Last Year ({{ new Date().getFullYear() - 1 }})</option>
          </select>
        </div>
      </div>

      <div class="date-range-inputs">
        <div class="input-field">
          <label class="field-label">From</label>
          <input type="date" v-model="fromDate" @input="selectedPreset = ''" class="custom-input" />
        </div>
        <div class="input-field">
          <label class="field-label">To</label>
          <input type="date" v-model="toDate" @input="selectedPreset = ''" class="custom-input" />
        </div>
        <button class="reset-btn" @click="resetDates">Reset</button>
      </div>
    </div>

    <!-- ================= CHARTS ================= -->
    <div class="chart-card two-charts">
      <!-- LEFT: Broker-wise -->
      <div class="chart-half">
        <Line
          v-if="loaded"
          :data="brokerChart.data"
          :options="brokerChart.options"
          :plugins="chartPlugins"
        />
      </div>

      <!-- RIGHT: Portfolio Summary -->
      <div class="chart-half">
        <Line
          v-if="loaded"
          :data="summaryChart.data"
          :options="summaryChart.options"
          :plugins="chartPlugins"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

import { storeToRefs } from 'pinia';
import { useAlertStore, platformStore } from '@/stores';
import gql from 'graphql-tag';

const FILTER_STORAGE_KEY = 'statistics_chart_filters';

const STATISTICS_CHART_QUERY = gql`
  query StatisticsChart($platformIds: [ID]) {
    statisticsChart(platform_ids: $platformIds) {
      stock_investment
      stock_profit_loss
      updated_at
    }
  }
`;

const STATISTICS_CHART_PLATFORM_WISE = gql`
query StatisticsChartPlatformWise($platformIds: [ID]) {
  statisticsChartPlatformWise(platform_ids: $platformIds) {
    name
    investment
    profit_loss
    updated_at
    statistics {
      stock_investment
      stock_profit_loss
      updated_at
    }
  }
}`;

export default {
  name: 'StatisticsChart',
  components: { Line },

  data: () => ({
    loaded: false,
    date: 'DAY',
    fromDate: '',
    toDate: '',
    selectedPreset: '',

    brokerChart: { data: { labels: [], datasets: [] }, options: {} },
    summaryChart: { data: { labels: [], datasets: [] }, options: {} },

    baseChartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      spanGaps: true,
      layout: { padding: { right: 80 } },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { usePointStyle: true, padding: 20, font: { size: 12, weight: '600' } }
        },
        tooltip: {
          callbacks: {
            label: ctx => ` ${ctx.dataset.label}: ₹${ctx.parsed.y.toLocaleString()}`
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 8,
            minRotation: 0,
            maxRotation: 45
          }
        },
        y: { grid: { color: '#f1f5f9' } }
      }
    },

    colors: ['#2563EB', '#00c4ae', '#228377', '#324946', '#8f4f00', '#d948ae']
  }),

  methods: {
    formatLocalDate(date) {
      const offset = date.getTimezoneOffset();
      return new Date(date.getTime() - offset * 60000).toISOString().split('T')[0];
    },

    parsePlatformOptions() {
      const raw = this.platformOptions && typeof this.platformOptions === 'object' && 'value' in this.platformOptions
        ? this.platformOptions.value
        : this.platformOptions;

      if (!raw) return [];
      if (Array.isArray(raw)) return raw;

      try {
        return JSON.parse(raw);
      } catch (error) {
        return typeof raw === 'string'
          ? raw.split(',').map(item => item.trim()).filter(Boolean)
          : [];
      }
    },

    toTimestamp(value) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return numericValue;
      }

      const parsedValue = new Date(value).getTime();
      return Number.isFinite(parsedValue) ? parsedValue : null;
    },

    getStartOfWeek(date) {
      const start = new Date(date);
      const day = start.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diff);
      start.setHours(0, 0, 0, 0);
      return start;
    },

    getBucketStart(timestamp) {
      const date = new Date(timestamp);
      date.setHours(0, 0, 0, 0);

      if (this.date === 'MONTH') {
        return new Date(date.getFullYear(), date.getMonth(), 1).getTime();
      }

      if (this.date === 'WEEK') {
        return this.getStartOfWeek(date).getTime();
      }

      return date.getTime();
    },

    getBucketLabel(bucketStart) {
      const date = new Date(bucketStart);

      if (this.date === 'MONTH') {
        return new Intl.DateTimeFormat('en-GB', {
          month: 'short',
          year: 'numeric'
        }).format(date);
      }

      if (this.date === 'WEEK') {
        const end = new Date(bucketStart);
        end.setDate(end.getDate() + 6);
        const formatter = new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: 'short'
        });

        return `${formatter.format(date)} - ${formatter.format(end)}`;
      }

      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short'
      }).format(date);
    },

    getMetricValue(type, investment, profitLoss) {
      if (type === 'INV') return investment;
      if (type === 'PL') return profitLoss;
      return investment + profitLoss;
    },

    buildSeriesMap(rows, type, isWithinRange) {
      const series = new Map();

      rows.forEach(row => {
        const updatedAt = this.toTimestamp(row.updated_at);
        if (!Number.isFinite(updatedAt) || !isWithinRange(updatedAt)) {
          return;
        }

        const investment = Number(row.stock_investment) || 0;
        const profitLoss = Number(row.stock_profit_loss) || 0;
        const bucketStart = this.getBucketStart(updatedAt);
        const currentEntry = series.get(bucketStart);

        if (!currentEntry || updatedAt >= currentEntry.updatedAt) {
          series.set(bucketStart, {
            updatedAt,
            value: this.getMetricValue(type, investment, profitLoss)
          });
        }
      });

      return series;
    },

    upsertSeriesValue(series, updatedAtValue, value, isWithinRange) {
      const updatedAt = this.toTimestamp(updatedAtValue);
      if (!Number.isFinite(updatedAt) || !isWithinRange(updatedAt)) {
        return;
      }

      const bucketStart = this.getBucketStart(updatedAt);
      const currentEntry = series.get(bucketStart);

      if (!currentEntry || updatedAt >= currentEntry.updatedAt) {
        series.set(bucketStart, {
          updatedAt,
          value
        });
      }
    },

    saveFilters() {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
        date: this.date,
        fromDate: this.fromDate,
        toDate: this.toDate,
        selectedPreset: this.selectedPreset
      }));
    },

    restoreFilters() {
      const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
      if (!savedFilters) return;

      try {
        const parsed = JSON.parse(savedFilters);
        this.date = parsed.date || 'DAY';
        this.fromDate = parsed.fromDate || '';
        this.toDate = parsed.toDate || '';
        this.selectedPreset = parsed.selectedPreset || '';
      } catch (error) {
        localStorage.removeItem(FILTER_STORAGE_KEY);
      }
    },

    applyPreset() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let start = new Date(today);
      let end = new Date(today);

      switch (this.selectedPreset) {
        case 'TODAY':
          break;
        case 'YESTERDAY':
          start = new Date(today);
          start.setDate(today.getDate() - 1);
          end = new Date(start);
          break;
        case '7_DAYS':
          start.setDate(today.getDate() - 6);
          break;
        case '15_DAYS':
          start.setDate(today.getDate() - 14);
          break;
        case '30_DAYS':
          start.setDate(today.getDate() - 29);
          break;
        case '90_DAYS':
          start.setDate(today.getDate() - 89);
          break;
        case 'THIS_WEEK':
          start = this.getStartOfWeek(today);
          break;
        case 'LAST_WEEK':
          end = this.getStartOfWeek(today);
          end.setDate(end.getDate() - 1);
          start = this.getStartOfWeek(end);
          break;
        case 'THIS_MONTH': start = new Date(today.getFullYear(), today.getMonth(), 1); break;
        case 'LAST_MONTH':
          start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          end = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        case '3_MONTHS': start.setMonth(today.getMonth() - 3); break;
        case '6_MONTHS': start.setMonth(today.getMonth() - 6); break;
        case 'THIS_YEAR': start = new Date(today.getFullYear(), 0, 1); break;
        case 'LAST_YEAR':
          start = new Date(today.getFullYear() - 1, 0, 1);
          end = new Date(today.getFullYear() - 1, 11, 31);
          break;
        default: return;
      }

      this.fromDate = this.formatLocalDate(start);
      this.toDate = this.formatLocalDate(end);
    },

    resetDates() {
      this.fromDate = '';
      this.toDate = '';
      this.selectedPreset = '';
    },

    handleFilterChange() {
      this.saveFilters();
      this.updateChartData();
    },

    updateChartData() {
      const main = Array.isArray(this.statisticsChart) ? this.statisticsChart : [];
      const platforms = Array.isArray(this.statisticsChartPlatformWise) ? this.statisticsChartPlatformWise : [];
      this.loaded = false;

      const from = this.fromDate
        ? new Date(`${this.fromDate}T00:00:00`).getTime()
        : 0;
      const to = this.toDate
        ? new Date(`${this.toDate}T23:59:59.999`).getTime()
        : Infinity;

      const isWithinRange = timestamp => Number.isFinite(timestamp) && timestamp >= from && timestamp <= to;

      const totalSeries = this.buildSeriesMap(main, 'TOT', isWithinRange);
      const investmentSeries = this.buildSeriesMap(main, 'INV', isWithinRange);
      const profitLossSeries = this.buildSeriesMap(main, 'PL', isWithinRange);

      const liveSummary = platforms.reduce((summary, platform) => {
        const updatedAt = this.toTimestamp(platform.updated_at);
        if (!Number.isFinite(updatedAt)) {
          return summary;
        }

        summary.investment += Number(platform.investment) || 0;
        summary.profitLoss += Number(platform.profit_loss) || 0;
        summary.updatedAt = Math.max(summary.updatedAt, updatedAt);
        return summary;
      }, {
        investment: 0,
        profitLoss: 0,
        updatedAt: Number.NEGATIVE_INFINITY
      });

      if (Number.isFinite(liveSummary.updatedAt)) {
        this.upsertSeriesValue(
          totalSeries,
          liveSummary.updatedAt,
          this.getMetricValue('TOT', liveSummary.investment, liveSummary.profitLoss),
          isWithinRange
        );
        this.upsertSeriesValue(
          investmentSeries,
          liveSummary.updatedAt,
          this.getMetricValue('INV', liveSummary.investment, liveSummary.profitLoss),
          isWithinRange
        );
        this.upsertSeriesValue(
          profitLossSeries,
          liveSummary.updatedAt,
          this.getMetricValue('PL', liveSummary.investment, liveSummary.profitLoss),
          isWithinRange
        );
      }

      const brokerSeries = platforms.map((platform, index) => {
        const series = this.buildSeriesMap(platform.statistics || [], 'TOT', isWithinRange);
        const investment = Number(platform.investment) || 0;
        const profitLoss = Number(platform.profit_loss) || 0;

        this.upsertSeriesValue(
          series,
          platform.updated_at,
          this.getMetricValue('TOT', investment, profitLoss),
          isWithinRange
        );

        return {
          label: platform.name,
          borderColor: this.colors[(index + 1) % this.colors.length],
          series
        };
      });

      const bucketStarts = new Set();
      [totalSeries, investmentSeries, profitLossSeries].forEach(series => {
        series.forEach((_, bucketStart) => bucketStarts.add(bucketStart));
      });
      brokerSeries.forEach(({ series }) => {
        series.forEach((_, bucketStart) => bucketStarts.add(bucketStart));
      });

      const sortedBucketStarts = Array.from(bucketStarts).sort((a, b) => a - b);
      const labels = sortedBucketStarts.map(bucketStart => this.getBucketLabel(bucketStart));
      const seriesToData = series => sortedBucketStarts.map(bucketStart => series.get(bucketStart)?.value ?? null);

      this.summaryChart.data = {
        labels,
        datasets: [
          { label: 'Total Value', data: seriesToData(totalSeries), borderColor: this.colors[0], borderWidth: 5, tension: .3 },
          { label: 'Investment', data: seriesToData(investmentSeries), borderColor: '#16a34a', borderDash: [6, 4], borderWidth: 3 },
          { label: 'Profit / Loss', data: seriesToData(profitLossSeries), borderColor: '#ef4444', borderDash: [6, 4], borderWidth: 3 }
        ]
      };

      this.brokerChart.data = {
        labels,
        datasets: brokerSeries.map(({ label, borderColor, series }) => ({
          label,
          data: seriesToData(series),
          borderColor,
          borderWidth: 3,
          tension: .3
        }))
      };

      this.summaryChart.options = this.baseChartOptions;
      this.brokerChart.options = this.baseChartOptions;
      this.loaded = true;
    }
  },

  watch: {
    statisticsChart: 'updateChartData',
    statisticsChartPlatformWise: 'updateChartData',
    selectedPreset: 'saveFilters',
    date: 'handleFilterChange',
    fromDate: 'handleFilterChange',
    toDate: 'handleFilterChange'
  },

  created() {
    this.restoreFilters();
  },

  setup() {
    const store = platformStore();
    const { platform } = storeToRefs(store);
    return { alertStore$: useAlertStore(), platformOptions: platform };
  },

  apollo: {
    statisticsChart: {
      query: STATISTICS_CHART_QUERY,
      variables() {
        return { platformIds: this.parsePlatformOptions() };
      }
    },
    statisticsChartPlatformWise: {
      query: STATISTICS_CHART_PLATFORM_WISE,
      variables() {
        return { platformIds: this.parsePlatformOptions() };
      }
    }
  }
};
</script>

<style scoped>

.chart-container-root { padding: 15px; background: var(--light); border-radius: var(--radius-lg); font-family: var(--font-sans); }
.filter-header { display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: 15px; margin-bottom: 25px; }
.filter-controls-left { display: flex; align-items: center; gap: 12px; }
.segmented-control { display: flex; background: var(--border); padding: 4px; border-radius: var(--radius-md); }
.segmented-control button { border: none; background: transparent; padding: 8px 14px; border-radius: 7px; font-size: 13px; font-weight: 600; color: var(--text-muted); cursor: pointer; }
.segmented-control button.active { background: #fff; color: var(--primary); box-shadow: var(--shadow-sm); }
.custom-select { padding: 9px 12px; border-radius: var(--radius-md); border: 1px solid var(--border); background: white; font-size: 13px; outline: none; }
.date-range-inputs { display: flex; gap: 12px; align-items: flex-end; }
.input-field { display: flex; flex-direction: column; }
.field-label { font-size: 11px; font-weight: 700; color: var(--light-dark); text-transform: uppercase; margin-bottom: 4px; }
.custom-input { padding: 7px 10px; border: 1px solid var(--border); border-radius: 8px; font-size: 13px; }
.reset-btn { background: white; border: 1px solid var(--danger); color: var(--danger); padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; }
.chart-card { background: white; border-radius: var(--radius-lg); padding: 20px; border: 1px solid var(--border); box-shadow: var(--shadow-sm); }
.chart-fixed-container { height: 500px; width: 100%; }

.two-charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.chart-half {
  height: 500px;
}

@media (max-width: 1024px) {
  .two-charts {
    grid-template-columns: 1fr;
  }
}
</style>
